import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { Box, ButtonBase, Card, CardContent, Chip, IconButton, Link, Menu, MenuItem, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { elapsedMsSince, formatElapsedLabel } from "../util/scribePresence";
import { VENUE_SHORT_OPTIONS } from "../util/classDisplay";
import { buildScribeBlocks, oldestUnsentParts } from "../util/stationCard";
import useVenueDateTime from "../hooks/useVenueDateTime";

// Placeholder glyph for an empty value (U+2014), escaped so source stays dash-free.
const EMPTY_CELL = "\u2014";
const MIDDLE_DOT = "\u00b7";

function elapsedFrom(iso, now, t) {
  const label = formatElapsedLabel(elapsedMsSince(iso, now));
  if (label === null) return t("stationMonitor.never");
  return t("stationMonitor.lastSeen", { time: label });
}

// #3548: one station as an outlined card. Header (name, id tail, chips, gear
// menu), a last-seen / device-health line, then ONE block per scribe: a
// label/value list (Scribe, Reconcile), one section per ring stretch (Ring,
// Position, On this ring) and four count boxes. No table, no column headings.
// The four buttons the old row carried are one gear menu with the same show
// rules, plus "Name station" for a station that has no name yet (#3547).
export default function StationCard({
  group,
  now,
  t,
  canClear,
  pendingId,
  alreadyCleared,
  onRename,
  onRestore,
  onClear,
  onClearMachine,
  onSeeBlocked,
}) {
  const headerKey = group.stationName ?? "unnamed";
  const displayName = group.stationName ?? t("stationMonitor.stationUnnamed");
  const formatVenueLocalDateTime = useVenueDateTime();
  const [anchorEl, setAnchorEl] = useState(null);
  const closeMenu = () => setAnchorEl(null);
  const pick = (fn) => () => {
    closeMenu();
    fn();
  };

  const menuItems = [];
  if (canClear && !group.stationName) {
    menuItems.push(
      <MenuItem key="name" onClick={pick(() => onRename(group))} data-testid={`station-monitor-rename-${headerKey}`}>
        {t("stationMonitor.menu.nameStation")}
      </MenuItem>,
    );
  }
  if (canClear && group.stationName) {
    menuItems.push(
      <MenuItem key="rename" onClick={pick(() => onRename(group))} data-testid={`station-monitor-rename-${headerKey}`}>
        {t("stationMonitor.menu.rename")}
      </MenuItem>,
    );
  }
  if (canClear && (group.cleared || group.retired)) {
    menuItems.push(
      <MenuItem
        key="current"
        disabled={pendingId !== null}
        onClick={pick(() => onRestore(group.key))}
        data-testid={`station-monitor-restore-${headerKey}`}
      >
        {t("stationMonitor.menu.moveToCurrent")}
      </MenuItem>,
    );
  }
  if (canClear && !group.cleared && group.machineClear?.status !== "done") {
    menuItems.push(
      <MenuItem
        key="reset"
        onClick={pick(() => onClearMachine(group))}
        sx={{ color: "warning.dark" }}
        data-testid={`station-monitor-clear-machine-${headerKey}`}
      >
        {t("stationMonitor.menu.resetTablet")}
      </MenuItem>,
    );
  }
  if (canClear && !group.cleared && !group.retired && !group.live) {
    menuItems.push(
      <MenuItem key="older" onClick={pick(() => onClear(group))} data-testid={`station-monitor-clear-${headerKey}`}>
        {t("stationMonitor.menu.moveToOlder")}
      </MenuItem>,
    );
  }

  const blocks = buildScribeBlocks(group.rows);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid={`station-monitor-station-${headerKey}`}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
            <Typography variant="h6" color="text.primary" component="h2" data-testid="station-monitor-name">
              {displayName}
            </Typography>
            {group.stationIdSuffix && (
              <Typography variant="caption" color="text.secondary" component="span" data-testid="station-monitor-id-tail">
                {`\u2026${group.stationIdSuffix}`}
              </Typography>
            )}
            {group.live && !group.retired && (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label={t("stationMonitor.live")}
                data-testid={`station-monitor-live-${headerKey}`}
              />
            )}
            {group.isTest && <Chip size="small" variant="outlined" label={t("stationMonitor.test.chip")} />}
            {group.oldBuild && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t("stationBuild.flag")}
                title={t("stationBuild.detail", { build: group.appBuild, live: group.liveBuild })}
                data-testid={`station-monitor-old-build-${headerKey}`}
              />
            )}
          </Box>
          {menuItems.length > 0 && (
            <>
              <IconButton
                size="small"
                aria-label={t("stationMonitor.menu.actions", { name: displayName })}
                aria-haspopup="menu"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                data-testid={`station-monitor-gear-${headerKey}`}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={closeMenu}>
                {menuItems}
              </Menu>
            </>
          )}
        </Box>

        <Box
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, mt: 0.5 }}
          data-testid={`station-monitor-device-health-${headerKey}`}
        >
          <Typography variant="body2" color="text.secondary" component="span" data-testid={`station-monitor-last-seen-${headerKey}`}>
            {elapsedFrom(group.lastSeenAt, now, t)}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="span" aria-hidden="true">
            {MIDDLE_DOT}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="span">
            {t("stationMonitor.fields.deviceHealth")}
          </Typography>
          <DeviceHealthCell health={group.deviceHealth} t={t} testKey={headerKey} />
        </Box>

        {group.cleared && (
          <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }} data-testid={`station-monitor-cleared-${headerKey}`}>
            {t("stationMonitor.clear.clearedBy", {
              by: group.clearedByName || t("stationMonitor.reconcile.reviewerUnknown"),
              time: formatVenueLocalDateTime(group.clearedAt),
            })}
          </Typography>
        )}
        <MachineClearStatus group={group} t={t} headerKey={headerKey} alreadyCleared={alreadyCleared === group.key} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
          {blocks.map((block) => (
            <ScribeBlock key={block.key} block={block} group={group} now={now} t={t} onSeeBlocked={onSeeBlocked} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// A label / value list. Plain definition list, so there is no table and no
// column heading to scroll sideways on a phone.
function Fields({ children, ...rest }) {
  return (
    <Box
      component="dl"
      sx={{ m: 0, display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 2, rowGap: 0.25 }}
      {...rest}
    >
      {children}
    </Box>
  );
}

function Field({ label, children, testId }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" color="text.primary" sx={{ m: 0, overflowWrap: "anywhere" }} data-testid={testId}>
        {children}
      </Typography>
    </>
  );
}

function rangeLabel(stretch, t, formatShort) {
  if (!stretch.from) return EMPTY_CELL;
  const from = formatShort(stretch.from);
  if (stretch.end?.ongoing) return t("stationMonitor.onThisRing.ongoing", { from });
  if (stretch.end?.at) return t("stationMonitor.onThisRing.range", { from, to: formatShort(stretch.end.at) });
  return from;
}

function ScribeBlock({ block, now, t, onSeeBlocked, group }) {
  const formatShort = useVenueDateTime(VENUE_SHORT_OPTIONS);
  const snapshot = block.snapshot;
  const reconcile = snapshot?.reconcile ?? null;
  const reconcileShown = reconcile && (reconcile.reviewed || (reconcile.disagreementCount ?? 0) > 0);
  const showScribe = Boolean(block.scribeName) || block.scribeSub !== null || block.stretches.length > 0;
  const scribeName = block.scribeName || t("common.unknownUser");
  const oldest = snapshot ? oldestUnsentParts(elapsedMsSince(snapshot.oldestUnshippedAt, now)) : null;
  const queued = snapshot?.queued ?? null;
  const blocked = snapshot?.needsAttention ?? null;

  return (
    <Box
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
      data-testid={`station-monitor-scribe-${block.key}`}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          {(showScribe || reconcileShown) && (
            <Fields>
              {showScribe && <Field label={t("stationMonitor.fields.scribe")}>{scribeName}</Field>}
              {reconcileShown && (
                <Field label={t("stationMonitor.fields.reconcile")} testId={`station-monitor-reconcile-${block.key}`}>
                  <ReconcileCell blockKey={block.key} reconcile={reconcile} t={t} />
                </Field>
              )}
            </Fields>
          )}
          {block.stretches.map((stretch) => (
            <Fields key={stretch.rowKey} data-testid={`station-monitor-row-${stretch.rowKey}`}>
              <Field label={t("stationMonitor.fields.ring")} testId={`station-monitor-ring-${stretch.rowKey}`}>
                {stretch.ring ?? EMPTY_CELL}
              </Field>
              <Field label={t("stationMonitor.fields.position")} testId={`station-monitor-position-${stretch.rowKey}`}>
                {stretch.position ?? EMPTY_CELL}
              </Field>
              <Field label={t("stationMonitor.fields.onThisRing")} testId={`station-monitor-worked-${stretch.rowKey}`}>
                {rangeLabel(stretch, t, formatShort)}
              </Field>
            </Fields>
          ))}
        </Box>

        <Box sx={{ flex: "2 1 360px", minWidth: 0 }}>
          {!snapshot ? (
            <Box data-testid={`station-monitor-never-uploaded-${block.key}`}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={
                  // #2827 judge fix r1 (FE item 2): a device that changed hands
                  // mid-show has snapshot history, just not under THIS scribe, so
                  // say so rather than implying the station itself is silent.
                  block.snapshotSeenElsewhere ? t("stationMonitor.noSnapshotForScribe") : t("stationMonitor.neverUploaded")
                }
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 1,
                // Fit as many boxes per row as keep the one-line title ("SEND BLOCKED") inside its box.
                // The room depends on the card, not the screen: the nav rail and page padding eat
                // most of a 390px phone, so a fixed 2-up grid clipped the titles (real Chrome, #3548).
                gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))",
              }}
            >
              <StatBox title={t("stationMonitor.boxes.sent")} value={snapshot.shipped ?? EMPTY_CELL} valueTestId={`station-monitor-shipped-${block.key}`} />
              <StatBox
                title={t("stationMonitor.boxes.unsent")}
                value={queued ?? EMPTY_CELL}
                warn={queued > 0}
                valueTestId={`station-monitor-queued-${block.key}`}
              >
                {/* #3526: ops this snapshot still lists that the server already
                    has. Not counted above; named here so staff see why. */}
                {snapshot.onServer > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    data-testid={`station-monitor-on-server-${block.key}`}
                  >
                    {t("stationMonitor.alreadyOnServer", { count: snapshot.onServer })}
                  </Typography>
                )}
              </StatBox>
              <StatBox
                title={t("stationMonitor.boxes.sendBlocked")}
                value={blocked ?? EMPTY_CELL}
                warn={blocked > 0}
                valueTestId={`station-monitor-needs-attention-${block.key}`}
                onClick={blocked > 0 ? () => onSeeBlocked({ group, block }) : undefined}
                buttonTestId={`station-monitor-send-blocked-${block.key}`}
              >
                {blocked > 0 && (
                  <Typography variant="caption" color="text.primary" component="div" sx={{ textDecoration: "underline" }}>
                    {t("stationMonitor.boxes.seeSheets")}
                  </Typography>
                )}
              </StatBox>
              <StatBox
                title={t("stationMonitor.boxes.oldestUnsent")}
                value={oldest ? <OldestUnsent parts={oldest} t={t} /> : EMPTY_CELL}
                warn={oldest !== null}
                valueTestId={`station-monitor-oldest-unshipped-${block.key}`}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Every key is a literal so the i18n extractor and the unused-key check see it.
function OldestUnsent({ parts, t }) {
  switch (parts.unit) {
    case "second":
      return t("stationMonitor.oldestUnsentValue.second", { count: parts.count });
    case "minute":
      return t("stationMonitor.oldestUnsentValue.minute", { count: parts.count });
    case "hour":
      return t("stationMonitor.oldestUnsentValue.hour", { count: parts.count });
    default:
      return t("stationMonitor.oldestUnsentValue.day", { count: parts.count });
  }
}

// One count box. Same size and alignment as its siblings: a one-line small-caps
// title over a big number. `warn` is the orange tint (Unsent, Send blocked and
// Oldest unsent when above 0); the card outline stays neutral. With `onClick`
// the whole box is a button.
function StatBox({ title, value, warn = false, valueTestId, onClick, buttonTestId, children }) {
  const inner = (
    <>
      <Typography
        component="div"
        color="text.secondary"
        sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap", lineHeight: 1.4 }}
      >
        {title}
      </Typography>
      <Typography component="div" color="text.primary" sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3 }} data-testid={valueTestId}>
        {value}
      </Typography>
      {children}
    </>
  );
  const sx = (theme) => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    minWidth: 0,
    p: 1,
    borderRadius: 1,
    border: 1,
    borderColor: warn ? "warning.main" : "divider",
    bgcolor: warn ? alpha(theme.palette.warning.main, 0.16) : "transparent",
  });
  if (onClick) {
    return (
      <ButtonBase onClick={onClick} sx={sx} data-warning={warn ? "true" : "false"} data-testid={buttonTestId}>
        <Box sx={{ width: "100%" }}>{inner}</Box>
      </ButtonBase>
    );
  }
  return (
    <Box sx={sx} data-warning={warn ? "true" : "false"}>
      {inner}
    </Box>
  );
}

// #3518: where a Reset tablet stands for this station, in plain words that say
// what staff should do. Static i18n keys only.
function MachineClearStatus({ group, t, headerKey, alreadyCleared }) {
  const mc = group.machineClear;
  if (!mc && !alreadyCleared) return null;
  const id = `station-monitor-machine-clear-${headerKey}`;
  if (alreadyCleared || mc?.status === "done") {
    return (
      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }} data-testid={id}>
        {t("stationMonitor.clearMachine.done")}
      </Typography>
    );
  }
  if (mc.status === "blocked") {
    const c = mc.blocked ?? {};
    return (
      <Box sx={{ mt: 0.5 }} data-testid={id}>
        <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 600 }}>
          {t("stationMonitor.clearMachine.blockedTitle")}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {t("stationMonitor.clearMachine.blockedWhat")}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }} data-testid={`${id}-counts`}>
          {c.outbox > 0 && <li>{t("stationMonitor.clearMachine.count.outbox", { count: c.outbox })}</li>}
          {c.held > 0 && <li>{t("stationMonitor.clearMachine.count.held", { count: c.held })}</li>}
          {c.unsavedEdits > 0 && <li>{t("stationMonitor.clearMachine.count.unsavedEdits", { count: c.unsavedEdits })}</li>}
          {c.localDrafts > 0 && <li>{t("stationMonitor.clearMachine.count.localDrafts", { count: c.localDrafts })}</li>}
          {c.manualRides > 0 && <li>{t("stationMonitor.clearMachine.count.manualRides", { count: c.manualRides })}</li>}
        </Box>
        <Typography variant="body2" color="text.primary">
          {t("stationMonitor.clearMachine.blockedWhatToDo")}
        </Typography>
      </Box>
    );
  }
  return (
    <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }} data-testid={id}>
      {t("stationMonitor.clearMachine.pending")}
    </Typography>
  );
}

// #3436 -- device health, on the station's second line. No health (an older
// scribe-fe build, or a station with no heartbeat) is a dash. A healthy device
// says OK. A flagged one shows a warning chip and lists every reason in plain
// words, visible without hovering (these are read on a tent laptop and a phone,
// where there is no hover).
function issueText(issue, t) {
  switch (issue.code) {
    case "storageNotPersistent":
      return t("stationDeviceHealth.issue.storageNotPersistent");
    case "storageNearlyFull":
      return t("stationDeviceHealth.issue.storageNearlyFull", { percent: issue.percent });
    case "storageFull":
      return t("stationDeviceHealth.issue.storageFull");
    case "authExpired":
      return t("stationDeviceHealth.issue.authExpired");
    case "syncStuck":
      return t("stationDeviceHealth.issue.syncStuck", { count: issue.count });
    case "offline":
      return t("stationDeviceHealth.issue.offline");
    default:
      return null;
  }
}

function DeviceHealthCell({ health, t, testKey }) {
  if (!health?.known) {
    return (
      <Typography variant="body2" color="text.secondary" component="span" data-testid={`station-monitor-device-health-none-${testKey}`}>
        {EMPTY_CELL}
      </Typography>
    );
  }
  if (health.issues.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" component="span" data-testid={`station-monitor-device-health-ok-${testKey}`}>
        {t("stationDeviceHealth.ok")}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "contents" }} data-testid={`station-monitor-device-health-flag-${testKey}`}>
      <Chip size="small" color="warning" variant="outlined" label={t("stationDeviceHealth.flag")} />
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2, flexBasis: "100%" }}>
        {health.issues.map((issue) => (
          <Typography key={issue.code} component="li" variant="caption" color="text.primary">
            {issueText(issue, t)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

// #3268 -- the reconcile value is status only; Station Monitor owns no reconcile
// action (the server auto-reconciles on every snapshot upload, and review lives
// in the Review Queue). Driven by the snapshot's reconcile state:
//   - never reconciled, or 0 disagreements: not shown at all.
//   - N > 0 unreviewed disagreements: "N disagreements", linking to the Review Queue.
//   - reviewed: who and when.
function ReconcileCell({ blockKey, reconcile, t }) {
  const formatVenueLocalDateTime = useVenueDateTime();
  if (reconcile.reviewed) {
    return (
      <span data-testid={`station-monitor-reconcile-status-${blockKey}`}>
        {t("stationMonitor.reconcile.reviewedBy", {
          // reviewedBy is a Cognito sub: show only the server-resolved name.
          by: reconcile.reviewedByName || t("stationMonitor.reconcile.reviewerUnknown"),
          time: formatVenueLocalDateTime(reconcile.reviewedAt),
        })}
      </span>
    );
  }
  return (
    <Link
      component={RouterLink}
      to="/review-queue"
      variant="body2"
      color="text.primary"
      data-testid={`station-monitor-reconcile-status-${blockKey}`}
    >
      {t("stationMonitor.reconcile.disagreementCount", { count: reconcile.disagreementCount ?? 0 })}
    </Link>
  );
}
